import { ModalMessageComponent } from './../controls/modal-message/modal-message.component';
import { Component, OnInit } from '@angular/core';
import { Subscription } from 'rxjs/internal/Subscription';
import { finalize, tap } from 'rxjs/operators';
import { SolutionInfo } from 'src/app/model/SolutionInfo';
import { DataTableService } from 'src/app/service/data-table.service';
import { EventService } from 'src/app/service/event.service';
import { UserResponse, UserService } from 'src/app/service/user.service';
import { Utils } from 'src/app/utils';
import { ActionMenuEvent, ActionMenuIcon, ActionMenuItem } from '../controls/actionmenu/action-menu.component';
import { ColumnSort, ColumnType, DataTableColumn, DataTableFilter, DataTableRow, FilterType } from '../controls/datatable/datatable.component';
import { Modal } from '../controls/modal/modal.service';
import { PageCount, PageEvent } from '../controls/pagination/pagination.component';
import { AuthenticationService } from 'src/app/service/authentication.service';
import { Events, MsgPayLoad } from 'src/app/service/websocket.service';
import { UserInfo } from 'src/app/model/User';
import { FormGroup } from '@angular/forms';
import { UserFormType } from '../user-form/user-form.component';
import { UserComponent, UserSaveResult, UserSaveResultType } from '../dialogs/user/user.component';

@Component({
    selector: 'app-settings-users',
    templateUrl: './settings-users.component.html',
    styleUrls: ['./settings-users.component.css'],
    standalone: false
})
export class SettingsUsersComponent implements OnInit {

  title: string = $localize`Users`;
  cols: DataTableColumn[] = [];
  rows: DataTableRow[] = [];
  loading: boolean = true;
  total: number = 0;
  userResponse: UserResponse;
  solutionInfo: SolutionInfo = new SolutionInfo();
  filters: DataTableFilter[] = [];
  paginationSize: number = PageCount.TWENTYFIVE;
  formType:UserFormType = UserFormType.LOCAL;
  
  currentUserAuthInfo : UserInfo;
  isSuperuser: boolean;
  userToEdit : UserInfo;
  userForm:FormGroup = new FormGroup({});

  //subscription
  solutionInfoSubscription: Subscription;
  applyFilterSubscription: Subscription;
  cancelFilterSubscription: Subscription;

  constructor(private userSrv: UserService,
              private eventService: EventService,
              private dataTableSrv: DataTableService, 
              private authService: AuthenticationService,
              private modal: Modal) {

    this.cols = Array<DataTableColumn>(
      new DataTableColumn("userName", $localize`Id`, ColumnType.STRING, true, "26", ColumnSort.DESC),
      new DataTableColumn("firstName", $localize`Given name`, ColumnType.STRING, false, "26"),
      new DataTableColumn("lastName", $localize`Surname`, ColumnType.STRING, false, "26"),
      new DataTableColumn("roleDisplayName", $localize`Role`, ColumnType.STRING, false, "22"),
    );

    this.filters = Array<DataTableFilter>(
      new DataTableFilter("user-filter-query", "query", $localize`Search Id, given name, or surname`, FilterType.STRING, undefined)
    );
    
    this.solutionInfoSubscription = this.eventService.currentSolutionInfo$.subscribe(newSolutionInfo => {
      this.solutionInfo = newSolutionInfo;
    });

    this.applyFilterSubscription = this.dataTableSrv.applyFilter$.subscribe((appliedFilters: DataTableFilter[]) => {
      this.getUsers(this.paginationSize, 0, appliedFilters[0].selected);
    });

    this.cancelFilterSubscription = this.dataTableSrv.cancelFilter$.subscribe(() => {
      this.getUsers(this.paginationSize, 0);
    });
  }

  ngOnInit(): void {
    this.currentUserAuthInfo = this.authService.getLoggedInUser();
    this.setformType();
    this.isSuperuser = Utils.isSuperuserRole(this.currentUserAuthInfo);
    this.getUsers(this.paginationSize, 0);
  }

  ngOnDestroy() {
    this.solutionInfoSubscription.unsubscribe();
    this.applyFilterSubscription.unsubscribe();
    this.cancelFilterSubscription.unsubscribe();
  }

  onPageChange(pageEvent: PageEvent) {
    this.getUsers(pageEvent.itemsPerPage, pageEvent.startingIndex);
  }

  updateUserForm(userForm:FormGroup): void {
    this.userForm = userForm;
  }
  
  cancel(): void {
    this.userToEdit = undefined;
    this.getUsers(this.paginationSize, 0);
  }

  actionMenuClicked(event: ActionMenuEvent): void {
    if(event.name === "Remove"){
      const sub: Subscription =
        this.modal.open(ModalMessageComponent, {
          data: {
            title: $localize`Remove`,
            message: event.data.length === 1 ? $localize`${event.data[0].userName} will be permanently removed. Remove selected user?` :
              $localize`${event.data.length} users will be permanently removed. Remove selected users?`,
            primaryBtn: $localize`Remove`,
            secondaryBtn: $localize`Cancel`,
            width: '350px'
          },
          id: 'otsc-users-remove-msg'
        }
        ).afterClosed.subscribe((isPrimaryBtnClicked: boolean) => {
          if (isPrimaryBtnClicked) {
            let users: string[] = [];

            if (event.data.length === 1) {
              users.push(event.data[0].userName);
            } else {
              event.data.forEach((data) => {
                users.push(data.userName);
              })
            }

            this.userSrv.removeUsers(users).pipe(
              tap(resp => {
                let msg;

                if (resp.body) {
                  msg = new MsgPayLoad(undefined, {
                    message: event.data.length === 1 ? $localize`${event.data[0].userName} was successfully removed.` :
                      $localize`${event.data.length} users were successfully removed.`
                  });
                  msg.setEvent(Events.SuccessMsg);
                } else {
                  msg = new MsgPayLoad(undefined, {
                    message: event.data.length === 1 ? $localize`The server encountered an error while removing the selected user. Please try again later or contact your system administrator.` :
                      $localize`The server encountered an error while removing the selected users. Please try again later or contact your system administrator.`
                  });
                  msg.setEvent(Events.ErrorMsg);
                }

                Utils.addHeaderMessageBanner(this.eventService, msg);
              }),
              finalize(() => {
                this.dataTableSrv.onResetPagination();
                this.getUsers(this.paginationSize, 0);
              })
            ).subscribe();
          }
          sub.unsubscribe();
        });
    }else if(event.name === 'Unlock'){
      const sub: Subscription =
        this.modal.open(ModalMessageComponent, {
          data: {
            title: $localize`Unlock`,
            message: $localize`${event.data[0].userName} account will be unlocked. Unlock selected user's account?`,
            primaryBtn: $localize`Unlock`,
            secondaryBtn: $localize`Cancel`,
            width: '350px'
          },
          id: 'otsc-users-unlock-msg'
        }
        ).afterClosed.subscribe((isPrimaryBtnClicked: boolean) => {
          if (isPrimaryBtnClicked) {
            this.userSrv.unlockAccount(event.data[0].userName).pipe(
              tap(resp => {
                let msg;

                if (resp.body.success) {
                  msg = new MsgPayLoad(undefined, {
                    message: $localize`${event.data[0].userName} account was successfully unlocked.`
                  });
                  msg.setEvent(Events.SuccessMsg);
                } else {
                  msg = new MsgPayLoad(undefined, {
                    message: $localize`The server encountered an error while unlocking the selected user's account. Please try again later or contact your system administrator.`
                  });
                  msg.setEvent(Events.ErrorMsg);
                }

                Utils.addHeaderMessageBanner(this.eventService, msg);
              }),
              finalize(() => {
                this.dataTableSrv.onResetPagination();
                this.getUsers(this.paginationSize, 0);
              })
            ).subscribe();
          }
          sub.unsubscribe();
        });
    }else if(event.name === "Edit"){
      this.setformType();
      this.userToEdit = event.data[0];
      const sub:Subscription = 
      this.modal.open(UserComponent, {
        data: {
          currentUserAuthInfo: this.userToEdit,
          formType: this.formType,
          formHeight: '300px'
        },
        id: 'otsc-settings-user-edit-dialog'
      }
      ).afterClosed.subscribe((result: UserSaveResult) => {
        this.userForm = result.form;
        let msg:MsgPayLoad = undefined;

        if (result.type == UserSaveResultType.SUCCESS) {
          msg = new MsgPayLoad(undefined,{
            message: $localize`${this.userForm.get('userNameCtrl').value} account was updated.`
          });
          msg.setEvent(Events.SuccessMsg);
        }else if(result.type == UserSaveResultType.FAILURE){
          msg = new MsgPayLoad(undefined,{
            message: $localize`The server encountered an error while updating the selected user's account. Please try again later or contact your system administrator.` 
          });
          msg.setEvent(Events.ErrorMsg);
        }

        this.dataTableSrv.onResetPagination();
        this.getUsers(this.paginationSize, 0, undefined, msg);
        sub.unsubscribe();
      });
    }
  }

  showAddUserModal():void {
    this.setformType();
    this.userToEdit = undefined;

    const sub:Subscription = 
      this.modal.open(UserComponent, {
        data: {
          currentUserAuthInfo: this.userToEdit,
          formType: this.formType,
          formHeight: '300px'
        },
        id: 'otsc-settings-user-add-dialog'
      }
      ).afterClosed.subscribe((result: UserSaveResult) => {
        this.userForm = result.form;
        let msg:MsgPayLoad = undefined;

        if (result.type == UserSaveResultType.SUCCESS) {
          msg = new MsgPayLoad(undefined,{
            message: $localize`${this.userForm.get('userNameCtrl').value} was added.`
          });
          msg.setEvent(Events.SuccessMsg);
        }else if(result.type == UserSaveResultType.FAILURE){
          msg = new MsgPayLoad(undefined,{
            message: $localize`The server encountered an error while adding the new user. Please try again later or contact your system administrator.` 
          });
          msg.setEvent(Events.ErrorMsg);
        }

        this.dataTableSrv.onResetPagination();
        this.getUsers(this.paginationSize, 0, undefined, msg);
        sub.unsubscribe();
      });
  }

  getUsers(pageSize: number, startIndex: number, query?: string, msg?:MsgPayLoad) {
    this.loading = true;

    this.userSrv.listUsers(pageSize, startIndex, query).pipe(
      tap(resp => {
        this.userResponse = resp.body;
        this.total = this.userResponse.count;
        var tmpRows: DataTableRow[] = [];
        this.rows = [];
        let row: DataTableRow;

        this.userResponse.users.forEach(user => {
          user.roleDisplayName = Utils.getUserRoleDisplayName(user.role);
          //handle superuser
          if(this.isSuperuser){
            if(this.currentUserAuthInfo.userName === user.userName){
              row = new DataTableRow(user, new Array<ActionMenuItem>(new ActionMenuItem("Edit", $localize`Edit`, ActionMenuIcon.EDIT, user, false, true)));
              row.disableCheckbox = true;
            }else{
              let actionMenu:ActionMenuItem[] = [new ActionMenuItem("Edit", $localize`Edit`, ActionMenuIcon.EDIT, user, false),
                                                  new ActionMenuItem("Remove", $localize`Remove`, ActionMenuIcon.DELETE, user, true)];
              if(this.formType == UserFormType.LOCAL){
                actionMenu.push(new ActionMenuItem("Unlock", $localize`Unlock`, ActionMenuIcon.UNLOCK, user, false));
              }

              row = new DataTableRow(user, actionMenu);
            }
          //handle admin user
          }else{
            if(user.role === "Superuser"){
              row = new DataTableRow(user, []);
              row.disableCheckbox = true;
            }else if(this.currentUserAuthInfo.userName === user.userName){
              row = new DataTableRow(user, new Array<ActionMenuItem>(new ActionMenuItem("Edit", $localize`Edit`, ActionMenuIcon.EDIT, user, false, true)));
              row.disableCheckbox = true;
            }else{
              let actionMenu:ActionMenuItem[] = [new ActionMenuItem("Edit", $localize`Edit`, ActionMenuIcon.EDIT, user, false),
                                                  new ActionMenuItem("Remove", $localize`Remove`, ActionMenuIcon.DELETE, user, true)];
              if(this.formType == UserFormType.LOCAL){
                actionMenu.push(new ActionMenuItem("Unlock", $localize`Unlock`, ActionMenuIcon.UNLOCK, user, false));
              }

              row = new DataTableRow(user, actionMenu);
            }
          }

          tmpRows.push(row);
        });
        this.rows = tmpRows;
      }),
      finalize(() => {
        if(msg){
          Utils.addHeaderMessageBanner(this.eventService, msg);
        }
        this.loading = false;
      })
    ).subscribe();
  }

  setformType():void {
    this.currentUserAuthInfo = this.authService.getLoggedInUser();
    this.formType = this.currentUserAuthInfo.settings.authenticationMethod == 'local' ? UserFormType.LOCAL : UserFormType.OTDS;
  }
}
